import React, { useMemo, useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { Check, ChevronLeft, ChevronRight, Search, Send, User, X } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { ChatService, ChatConversation } from '../lib/chatService';
import { ChatMessage } from '../types/dataModel';
import {
  formatChatDaySeparator,
  formatChatListTime,
  formatChatThreadTime,
  groupedChatSearch,
  isSameChatDay,
  isSystemChatMessage,
  matchRanges,
} from '../lib/chatUi';
import { PAGE_CHROME_OFF_WHITE } from '../constants/pageChrome';
import { displayNameFromProfile, getInitials } from './account/accountShared';
import { usePageStatusBar } from '../hooks/usePageStatusBar';
import { persistTrainerDashboardSection } from '../features/trainer/shell/trainerSectionNav';
import { CHAT_NAV_ENABLED } from '../lib/chatNavEnabled';
import './ChatIos.css';

/** Theme colors for avatar placeholders – one solid color per avatar (hash-based) */
const THEME_AVATAR_COLORS = ['#367BF4', 'var(--action-primary)', '#EB3F34', '#F7D046'] as const;
const LIGHT_ICON_COLORS = ['#fff', 'var(--ink)', '#fff', 'var(--ink)'] as const; /* green and yellow need dark type */

function getAvatarStyle(userId: string): React.CSSProperties {
  let n = 0;
  for (let i = 0; i < userId.length; i++) n += userId.charCodeAt(i);
  const idx = Math.abs(n) % THEME_AVATAR_COLORS.length;
  return {
    background: THEME_AVATAR_COLORS[idx],
    color: LIGHT_ICON_COLORS[idx],
  };
}

/** Trainers for slider (matches PersonalTrainers page) */
const SLIDER_TRAINERS = [
  { id: 1, name: 'Sarah Johnson', specialty: 'Weight Loss' },
  { id: 2, name: 'Mike Rodriguez', specialty: 'Strength' },
  { id: 3, name: 'Emily Chen', specialty: 'Yoga & Pilates' },
  { id: 4, name: 'David Thompson', specialty: 'Cardio' },
  { id: 5, name: 'Jessica Martinez', specialty: 'Rehabilitation' },
  { id: 6, name: 'Alex Kim', specialty: 'Functional' },
];

function conversationName(conv: ChatConversation): string {
  return displayNameFromProfile(conv.otherUser) || conv.otherUser.email || 'Member';
}

function HighlightedText({
  text,
  query,
  className,
}: {
  text: string;
  query: string;
  className?: string;
}) {
  const ranges = matchRanges(query, text);
  if (!query.trim() || ranges.length === 0) {
    return <span className={className}>{text}</span>;
  }
  const parts: React.ReactNode[] = [];
  let last = 0;
  ranges.forEach(([start, end], index) => {
    if (start > last) {
      parts.push(<span key={`t${index}`}>{text.slice(last, start)}</span>);
    }
    parts.push(
      <mark key={`m${index}`} className="chat-ios-mark">
        {text.slice(start, end)}
      </mark>,
    );
    last = end;
  });
  if (last < text.length) {
    parts.push(<span key="end">{text.slice(last)}</span>);
  }
  return <span className={className}>{parts}</span>;
}

function InitialsAvatar({ name, size }: { name: string; size: number }) {
  return (
    <div className="chat-ios-avatar" style={{ width: size, height: size, fontSize: size * 0.32 }}>
      {getInitials(name)}
    </div>
  );
}

export const Chat = ({ embedded = false }: { embedded?: boolean }) => {
  usePageStatusBar({ background: PAGE_CHROME_OFF_WHITE });
  const { profile, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const locationState = location.state as { openUserId?: string; openUserStatus?: 'active' | 'pending' } | null;
  const openUserId = locationState?.openUserId;
  const openUserStatus = locationState?.openUserStatus;
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [conversations, setConversations] = useState<ChatConversation[]>([]);
  const [selectedConversation, setSelectedConversation] = useState<ChatConversation | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchText, setSearchText] = useState('');
  const [searchFocused, setSearchFocused] = useState(false);

  const currentUserId = profile?.id || user?.id;
  const currentUserRole = profile?.role;
  const trimmedQuery = searchText.trim();
  const peopleSectionLabel = currentUserRole === 'trainer' ? 'Members' : 'Trainers';
  const searchPlaceholder =
    currentUserRole === 'trainer' ? 'Search members or messages' : 'Search trainers or messages';

  const searchable = useMemo(
    () =>
      conversations.map((conv) => ({
        conv,
        displayName: conversationName(conv),
        preview: conv.lastMessage?.content ?? 'No messages yet',
      })),
    [conversations],
  );

  const searchGroups = useMemo(
    () => groupedChatSearch(searchable, searchText),
    [searchable, searchText],
  );

  const loadConversations = React.useCallback(async () => {
    if (!currentUserId || !currentUserRole) return;
    try {
      setLoading(true);
      const convos = await ChatService.getConversations(currentUserId, currentUserRole as 'trainer' | 'member');
      setConversations(convos);
    } catch (err: any) {
      setError(err.message || 'Failed to load conversations');
    } finally {
      setLoading(false);
    }
  }, [currentUserId, currentUserRole]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    if (!currentUserId || !currentUserRole) return;
    const loadConversations = async () => {
      try {
        setLoading(true);
        const convos = await ChatService.getConversations(currentUserId, currentUserRole as 'trainer' | 'member');
        setConversations(convos);
        if (openUserId) {
          let conv = convos.find((c) => c.otherUserId === openUserId);
          if (conv) {
            setSelectedConversation(conv);
          } else {
            const otherUser = await ChatService.getOtherUser(openUserId);
            if (otherUser) {
              const newConv: ChatConversation = {
                otherUser,
                otherUserId: openUserId,
                ...(openUserStatus && { relationshipStatus: openUserStatus }),
              };
              setConversations((prev) => [...prev, newConv]);
              setSelectedConversation(newConv);
            }
          }
        }
      } catch (err: any) {
        setError(err.message || 'Failed to load conversations');
      } finally {
        setLoading(false);
      }
    };
    loadConversations();
  }, [currentUserId, currentUserRole, openUserId]);

  useEffect(() => {
    if (!currentUserId) return;
    const sub = ChatService.subscribeToAllIncomingMessages(currentUserId, (msg) => {
      setConversations((prev) => {
        const idx = prev.findIndex((c) => c.otherUserId === msg.senderId);
        if (idx === -1) {
          loadConversations();
          return prev;
        }
        const isViewingThisThread = selectedConversation?.otherUserId === msg.senderId;
        const lastMessage = isViewingThisThread ? { ...msg, readAt: new Date().toISOString() } : msg;
        const updated = prev.map((c) =>
          c.otherUserId === msg.senderId ? { ...c, lastMessage } : c
        );
        updated.sort((a, b) => {
          const at = a.lastMessage?.createdAt ?? '';
          const bt = b.lastMessage?.createdAt ?? '';
          return bt.localeCompare(at);
        });
        return updated;
      });
    });
    return () => sub.unsubscribe();
  }, [currentUserId, loadConversations, selectedConversation?.otherUserId]);

  useEffect(() => {
    if (!currentUserId || !selectedConversation) return;
    const loadMessages = async () => {
      try {
        const msgs = await ChatService.getMessages(currentUserId, selectedConversation.otherUserId);
        setMessages(msgs);
        setError(null);
        await ChatService.markAsRead(currentUserId, selectedConversation.otherUserId);
        window.dispatchEvent(new CustomEvent('chat-marked-read'));
        const now = new Date().toISOString();
        setConversations((prev) =>
          prev.map((conv) =>
            conv.otherUserId === selectedConversation.otherUserId && conv.lastMessage
              ? { ...conv, lastMessage: { ...conv.lastMessage, readAt: now } }
              : conv
          )
        );
      } catch (err: any) {
        setError(err.message || 'Failed to load messages');
      }
    };
    loadMessages();

    const channel = ChatService.subscribeToMessages(
      currentUserId,
      selectedConversation.otherUserId,
      (msg) => {
        setMessages((prev) => [...prev, msg]);
        const readAt = new Date().toISOString();
        ChatService.markAsRead(currentUserId, selectedConversation.otherUserId).then(() => {
          window.dispatchEvent(new CustomEvent('chat-marked-read'));
        });
        setConversations((prev) =>
          prev.map((conv) =>
            conv.otherUserId === selectedConversation.otherUserId
              ? { ...conv, lastMessage: { ...msg, readAt } }
              : conv
          )
        );
      }
    );
    return () => ChatService.unsubscribe(channel);
  }, [currentUserId, selectedConversation?.otherUserId]);

  useEffect(() => {
    if (embedded || currentUserRole !== 'trainer') return;
    persistTrainerDashboardSection(CHAT_NAV_ENABLED ? 'chat' : 'members');
    navigate('/trainer-dashboard', { replace: true });
  }, [embedded, currentUserRole, navigate]);

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newMessage.trim() || !currentUserId || !selectedConversation || sending) return;
    try {
      setSending(true);
      const msg = await ChatService.sendMessage(currentUserId, selectedConversation.otherUserId, newMessage.trim());
      setMessages((prev) => [...prev, msg]);
      setConversations((prev) =>
        prev.map((conv) =>
          conv.otherUserId === selectedConversation.otherUserId
            ? { ...conv, lastMessage: msg }
            : conv
        )
      );
      setNewMessage('');
    } catch (err: any) {
      setError(err.message || 'Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const openConversation = (conv: ChatConversation) => {
    setSearchFocused(false);
    setSelectedConversation(conv);
  };

  const renderConversationRow = (conv: ChatConversation) => {
    const name = conversationName(conv);
    const isSelected = selectedConversation?.otherUserId === conv.otherUserId;
    const unread = !!(
      conv.lastMessage &&
      conv.lastMessage.receiverId === currentUserId &&
      !conv.lastMessage.readAt &&
      !isSystemChatMessage(conv.lastMessage)
    );
    const preview = conv.lastMessage?.content ?? 'No messages yet';
    const systemPreview = conv.lastMessage ? isSystemChatMessage(conv.lastMessage) : false;
    return (
      <button
        key={conv.otherUserId}
        type="button"
        onClick={() => openConversation(conv)}
        className={`chat-ios-row ${isSelected ? 'is-selected' : ''}`}
      >
        <InitialsAvatar name={name} size={48} />
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline justify-between gap-2">
            <p
              className="truncate text-[16px] text-[var(--chat-ink)]"
              style={{ fontWeight: unread ? 600 : 500 }}
            >
              {name}
            </p>
            {conv.lastMessage && (
              <p className="shrink-0 text-[12px] text-[var(--chat-muted)]">
                {formatChatListTime(conv.lastMessage.createdAt)}
              </p>
            )}
          </div>
          <p
            className="truncate text-[14px]"
            style={{
              fontWeight: unread ? 600 : 400,
              fontStyle: systemPreview ? 'italic' : 'normal',
              color: unread ? 'var(--ink)' : 'var(--text-secondary)',
            }}
          >
            {preview}
          </p>
        </div>
        {unread && (
          <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-[var(--chat-accent)]" />
        )}
      </button>
    );
  };

  if (!currentUserId || !currentUserRole) {
    return (
      <div className="min-h-screen bg-theme-off-white dark:bg-theme-bg-dark flex items-center justify-center">
        <p className="text-theme-text dark:text-theme-off-white">Please log in to access chat.</p>
      </div>
    );
  }

  if (!embedded && currentUserRole === 'trainer') {
    return null;
  }

  const getBackLink = () => (currentUserRole === 'trainer' ? '/trainer-dashboard' : '/member-dashboard');

  return (
    <div className={`chat-ios flex overflow-hidden ${embedded ? 'h-full min-h-0' : 'h-screen pb-bottom-nav'}`}>
      <div
        className={`flex w-full flex-shrink-0 flex-col md:w-[390px] ${
          selectedConversation ? 'hidden md:flex' : 'flex'
        }`}
      >
        <div className="flex flex-col gap-3.5 px-5 pb-2.5 pt-5">
          {!searchFocused && (
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2 min-w-0">
                {!embedded && (
                  <button
                    type="button"
                    onClick={() => navigate(getBackLink())}
                    className="rounded-full p-1 text-[var(--chat-ink)]"
                    aria-label="Back"
                  >
                    <ChevronLeft className="h-5 w-5" />
                  </button>
                )}
                <h1 className="chat-ios-title">Chats</h1>
              </div>
              <button
                type="button"
                onClick={() => {
                  setSearchFocused(true);
                  searchInputRef.current?.focus();
                }}
                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[var(--chat-search)]"
                aria-label="Search chats"
              >
                <Search className="h-[18px] w-[18px]" strokeWidth={2.2} />
              </button>
            </div>
          )}
          <div className="flex items-center gap-2.5">
            <div className={`chat-ios-search flex-1 ${searchFocused ? 'is-focused' : ''}`}>
              <Search
                className="h-[17px] w-[17px] shrink-0"
                color={searchFocused ? 'var(--ink)' : 'var(--text-secondary)'}
                strokeWidth={2}
              />
              <input
                ref={searchInputRef}
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                onFocus={() => setSearchFocused(true)}
                placeholder={searchPlaceholder}
                aria-label={searchPlaceholder}
              />
              {searchText && (
                <button
                  type="button"
                  onClick={() => setSearchText('')}
                  className="shrink-0 text-[#D8D6D0]"
                  aria-label="Clear search"
                >
                  <X className="h-[17px] w-[17px]" />
                </button>
              )}
            </div>
            {searchFocused && (
              <button
                type="button"
                onClick={() => {
                  setSearchText('');
                  setSearchFocused(false);
                  searchInputRef.current?.blur();
                }}
                className="shrink-0 text-[16px] font-medium text-[var(--chat-ink)]"
              >
                Cancel
              </button>
            )}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="px-5 py-12 text-center text-sm text-[var(--chat-muted)]">Loading...</div>
          ) : currentUserRole === 'member' && !trimmedQuery ? (
            <>
              <div className="border-b border-[var(--chat-divider)] px-5 pb-6 pt-4">
                <h2 className="mb-4 text-lg font-heading font-semibold uppercase text-[var(--chat-ink)]">Find a trainer</h2>
                <div className="flex items-center gap-1 overflow-x-auto scrollbar-hide scroll-smooth-touch -mx-5 px-5">
                  {SLIDER_TRAINERS.map((t) => (
                    <Link key={t.id} to="/personal-trainers" className="flex min-w-[22.2vw] flex-shrink-0 flex-col items-center gap-1.5">
                      <div className="flex h-[min(21vw,5rem)] w-[min(21vw,5rem)] flex-shrink-0 items-center justify-center rounded-full [&>svg]:!h-[45%] [&>svg]:!w-[45%]" style={getAvatarStyle(String(t.id))}>
                        <User className="block" style={{ color: 'inherit' }} />
                      </div>
                      <span className="w-full truncate text-center text-xs text-[var(--chat-ink)]">{t.name.split(' ')[0]}</span>
                    </Link>
                  ))}
                  <Link to="/personal-trainers" className="flex min-w-[22.2vw] flex-shrink-0 flex-col items-center gap-1.5">
                    <div className="flex h-[min(21vw,5rem)] w-[min(21vw,5rem)] flex-shrink-0 items-center justify-center rounded-full border-2 border-dashed border-[var(--chat-divider)] bg-[var(--chat-search)] [&>svg]:!h-[45%] [&>svg]:!w-[45%]">
                      <ChevronRight className="block text-[var(--chat-meta)]" />
                    </div>
                    <span className="text-xs font-medium text-[var(--chat-muted)]">Browse</span>
                  </Link>
                </div>
              </div>
              {conversations.length === 0 ? (
                <div className="flex flex-col gap-6 px-5 pb-8 pt-8">
                  <p className="text-center text-sm text-[var(--chat-ink)]">
                    You have no active membership with a personal trainer to message them. Start searching
                  </p>
                  <Link
                    to="/personal-trainers"
                    className="flex w-full items-center justify-center gap-2 rounded-xl bg-[var(--chat-ink)] py-4 font-semibold text-white"
                  >
                    See more trainers
                    <ChevronRight className="h-5 w-5" />
                  </Link>
                </div>
              ) : (
                conversations.map(renderConversationRow)
              )}
            </>
          ) : conversations.length === 0 ? (
            <div className="px-5 py-10 text-[15px] text-[var(--chat-ink)]">
              <p>No conversations yet.</p>
              <p className="mt-1 text-sm text-[var(--chat-muted)]">Add members to start chatting.</p>
            </div>
          ) : trimmedQuery ? (
            searchGroups.people.length === 0 && searchGroups.messages.length === 0 ? (
              <p className="px-5 pt-6 text-sm text-[var(--chat-muted)]">
                No matching {peopleSectionLabel.toLowerCase()} or messages
              </p>
            ) : (
              <>
                {searchGroups.people.length > 0 && (
                  <>
                    <div className="chat-ios-section">{peopleSectionLabel}</div>
                    {searchGroups.people.map(({ conv, displayName }) => (
                      <button
                        key={`p-${conv.otherUserId}`}
                        type="button"
                        onClick={() => openConversation(conv)}
                        className="flex w-full items-center gap-3 px-5 py-2.5 text-left"
                      >
                        <InitialsAvatar name={displayName} size={44} />
                        <HighlightedText text={displayName} query={trimmedQuery} className="text-[16px]" />
                      </button>
                    ))}
                  </>
                )}
                {searchGroups.people.length > 0 && searchGroups.messages.length > 0 && (
                  <div className="mx-5 my-2 h-px bg-[var(--chat-divider)]" />
                )}
                {searchGroups.messages.length > 0 && (
                  <>
                    <div className="chat-ios-section">Messages</div>
                    {searchGroups.messages.map(({ conv, displayName, preview }) => (
                      <button
                        key={`m-${conv.otherUserId}`}
                        type="button"
                        onClick={() => openConversation(conv)}
                        className="flex w-full items-start gap-3 px-5 py-2.5 text-left"
                      >
                        <InitialsAvatar name={displayName} size={44} />
                        <div className="min-w-0">
                          <p className="truncate text-[15px] font-semibold">{displayName}</p>
                          <HighlightedText
                            text={preview}
                            query={trimmedQuery}
                            className="block truncate text-[14px] text-[var(--chat-meta)]"
                          />
                        </div>
                      </button>
                    ))}
                  </>
                )}
              </>
            )
          ) : (
            conversations.map(renderConversationRow)
          )}
        </div>
      </div>

      <div
        className={`flex min-w-0 flex-1 flex-col ${
          !selectedConversation ? 'hidden md:flex' : 'flex'
        }`}
      >
        {selectedConversation ? (
          <>
            <div className="flex items-center gap-2.5 border-b border-[var(--chat-divider)] px-4 pb-3 pt-4">
              <button
                type="button"
                onClick={() => setSelectedConversation(null)}
                className="p-1 text-[var(--chat-ink)] md:hidden"
                aria-label="Back to conversations"
              >
                <ChevronLeft className="h-[22px] w-[22px]" strokeWidth={2.2} />
              </button>
              <InitialsAvatar name={conversationName(selectedConversation)} size={36} />
              <div className="min-w-0">
                <p className="truncate text-[17px] font-semibold text-[var(--chat-ink)]">
                  {conversationName(selectedConversation)}
                </p>
                <p className="text-[12px] text-[var(--chat-meta)]">
                  {currentUserRole === 'trainer' ? 'Member' : 'Personal Trainer'}
                </p>
              </div>
            </div>

            <div className="flex-1 space-y-2.5 overflow-y-auto px-4 py-3.5">
              {messages.map((msg, index) => {
                const showDay = index === 0 || !isSameChatDay(messages[index - 1].createdAt, msg.createdAt);
                if (isSystemChatMessage(msg)) {
                  return (
                    <React.Fragment key={msg.id}>
                      {showDay && (
                        <div className="mb-0.5 text-center text-[12px] font-medium text-[var(--chat-meta)]">
                          {formatChatDaySeparator(msg.createdAt)}
                        </div>
                      )}
                      <div className="chat-ios-system">{msg.content}</div>
                    </React.Fragment>
                  );
                }
                const mine = msg.senderId === currentUserId;
                const isRead = Boolean(msg.readAt);
                return (
                  <React.Fragment key={msg.id}>
                    {showDay && (
                      <div className="mb-0.5 text-center text-[12px] font-medium text-[var(--chat-meta)]">
                        {formatChatDaySeparator(msg.createdAt)}
                      </div>
                    )}
                    <div className={`flex flex-col ${mine ? 'items-end' : 'items-start'}`}>
                      <div className={mine ? 'chat-ios-bubble-out' : 'chat-ios-bubble-in'}>{msg.content}</div>
                      {mine && (
                        <div className="mt-0.5 flex items-center gap-1 pr-0.5">
                          <span className="text-[11px] text-[var(--chat-meta)]">
                            {formatChatThreadTime(msg.createdAt)}
                          </span>
                          <span
                            className="inline-flex -space-x-1"
                            aria-label={isRead ? 'Read' : 'Sent'}
                          >
                            <Check
                              className="h-2.5 w-2.5"
                              strokeWidth={2.4}
                              color={isRead ? 'var(--success)' : '#B7B5AF'}
                            />
                            {isRead && (
                              <Check className="h-2.5 w-2.5" strokeWidth={2.4} color="var(--success)" />
                            )}
                          </span>
                        </div>
                      )}
                    </div>
                  </React.Fragment>
                );
              })}
              <div ref={messagesEndRef} />
            </div>

            <form onSubmit={handleSend} className="chat-ios-composer">
              <input
                type="text"
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                placeholder="Type a message"
                autoComplete="off"
                enterKeyHint="send"
                inputMode="text"
                aria-label="Message"
                disabled={sending}
              />
              <button
                type="submit"
                disabled={!newMessage.trim() || sending}
                className="chat-ios-send"
                aria-label="Send"
              >
                <Send className="h-4 w-4" />
              </button>
            </form>
            {error && (
              <p className="px-3 pb-2 text-[13px] text-red-600">{error}</p>
            )}
          </>
        ) : (
          <div className="flex flex-1 items-center justify-center">
            <p className="text-[15px] text-[var(--chat-meta)]">Select a conversation</p>
          </div>
        )}
      </div>
    </div>
  );
};
